import { useState, useEffect } from "react";
import Select, { components } from "react-select";
import { useTranslation } from "react-i18next";

import useStore from "../useStore";

// Largo del adelanto que se muestra en el tooltip. Es el unico recorte: el CSS
// no clampea lineas, porque recortar por alto deja letras cortadas a la mitad.
// Con el ancho del tooltip (320px) 120 caracteres entran en dos o tres lineas.
const NOTE_PREVIEW_LENGTH = 120

export const preview = note => {
  const single = note.replace(/\s+/g, ' ').trim()
  // Array.from corta por code points. slice() cuenta unidades UTF-16 y parte al
  // medio los pares surrogate (emoji), dejando un caracter roto antes del "…".
  const chars = Array.from(single)
  if (chars.length <= NOTE_PREVIEW_LENGTH) return single
  return `${chars.slice(0, NOTE_PREVIEW_LENGTH).join('').trimEnd()}…`
}

const Option = ({ children, ...props }) => {
  const { capacity, active, note } = props.data || {}
  const isActive = !!(capacity || active)
  return (
    <components.Option {...props}>
      <div
        className="d-flex align-items-center gap-2"
        data-tooltip-id={note ? 'internal-note-tooltip' : undefined}
        data-tooltip-content={note ? preview(note) : undefined}
      >
        <div>{active > 0 ? '✅' : '❗'}</div>
        <div className="text-truncate text-nowrap flex-fill">{children}</div>
      </div>
    </components.Option>
  )
}

const control = highlight => ({ children, ...props }) => {
  const className = props.className?.split(' ') || []
  if (highlight) className.push('is-invalid')
  return (
    <components.Control {...props} className={className.join(' ')}>
      {children}
    </components.Control>
  )
}

const Component = (props) => {
  const { t } = useTranslation()
  const isFetching = useStore(state => !!state.isFetching);

  const key = props.valueKey || "id";

  const onChange = value => {
    props.onChange(value);
  };

  const isLoading = props.isLoading ?? isFetching

  if (!props.show) return null

  return (
    <Select
      isClearable={!isLoading}
      isDisabled={isLoading || props.isDisabled}
      value={props.value}
      onChange={onChange}
      options={props.data}
      isLoading={isLoading}
      // Bug real de accesibilidad (regresión detectada en CI, 2026-08-10):
      // react-select NO usa el atributo HTML `placeholder` del <input> real
      // para el texto visible — lo dibuja aparte en un <div>. El input de
      // búsqueda queda sin nombre accesible (sin label implícito/explícito,
      // sin aria-label, sin placeholder real) — axe lo marca "critical".
      // aria-label sí lo soporta react-select y lo aplica al input interno.
      aria-label={props.placeholder}
      placeholder={props.placeholder}
      noOptionsMessage={() => props.noOptionsMessage ?? t('no_options', 'Sin opciones')}
      loadingMessage={() => props.loadingMessage ?? t('loading', 'Cargando')}
      getOptionValue={(item) => item[key]}
      components={{
        IndicatorSeparator: () => null,
        Option: params => <Option {...params} />,
        Control: control(props.highlight)
      }}
      styles={{
        singleValue: (provided, state) => ({
          ...provided,
          fontSize: 14,
          color: "var(--bs-primary)",
        }),
        option: (provided, state) => ({
          ...provided,
          fontSize: 14,
        }),
        placeholder: (provided, state) => ({
          ...provided,
          fontSize: 14,
          color: "#aaa",
        }),
        menuList: (provided) => ({
          ...provided,
          padding: 0,
        }),
        clearIndicator: (provided) => ({
          ...provided,
          marginRight: "-10px",
        }),
      }}
    />
  );
};

export default Component;
